import React, { useMemo } from 'react';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { motion } from 'framer-motion';
import { containerVariants, itemVariants } from '../shared/animationVariants';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { getGuardTelemetry } from '../../../practice';
import { Activity, ShieldAlert, BarChart2, AlertTriangle, Target } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
export function SessionAnalyticsPage() {
  const {
    accounts,
    getLearnerData
  } = useAppStore();
  const data = useMemo(() => {
    const allAccounts = accounts?.accountsById ? Object.values(accounts.accountsById) : [];
    const learners = allAccounts.filter(a => a.role === 'learner');
    let totalAttempts = 0;
    let correctAttempts = 0;
    let frustrationEvents = 0;
    const tierCounts: Record<number, number> = {};
    learners.forEach(learner => {
      const lData = getLearnerData(learner.id);
      if (!lData?.familyMastery) return;
      Object.values(lData.familyMastery).forEach(mastery => {
        if (!mastery.history) return;
        let consecutiveErrors = 0;
        mastery.history.forEach(attempt => {
          totalAttempts++;
          if (attempt.isCorrect) {
            correctAttempts++;
            consecutiveErrors = 0;
          } else {
            consecutiveErrors++;
            if (consecutiveErrors === 3) {
              frustrationEvents++;
            }
          }
          if (attempt.tier) {
            tierCounts[attempt.tier] = (tierCounts[attempt.tier] || 0) + 1;
          }
        });
      });
    });
    const guardData = getGuardTelemetry();
    return {
      totalAttempts,
      accuracy: totalAttempts > 0 ? Math.round(correctAttempts / totalAttempts * 100) : 0,
      frustrationEvents,
      tierCounts,
      guardData
    };
  }, [accounts, getLearnerData]);
  const maxTierCount = Math.max(...Object.values(data.tierCounts), 1);
  if (data.totalAttempts === 0 && data.guardData.totalSessions === 0) {
    return <PageLayout title="Sessie Analytics" subtitle="Inzicht in het gedrag van de Adaptive Engine (P-15)" data-id="element-7053">
        <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-7054">
          <BarChart2 className="w-12 h-12 text-slate-300 mb-4" data-id="element-7055" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2" data-id="element-7056">
            Nog geen sessiedata
          </h3>
          <p className="text-sm text-slate-500 max-w-md" data-id="element-7057">
            Zodra leerlingen oefensessies voltooien, verschijnen hier
            statistieken over accuratesse, tier distributie en guard telemetrie.
          </p>
        </div>
      </PageLayout>;
  }
  return <PageLayout title="Sessie Analytics" subtitle="Inzicht in het gedrag van de Adaptive Engine (P-15)" data-id="element-7058">
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-7059">
        <motion.div variants={itemVariants} data-id="element-7060">
          <StatCard label="Gemiddelde Accuratesse" value={`${data.accuracy}%`} icon={<Target className="w-4 h-4 text-emerald-500" data-id="element-7062" />} data-id="element-7061" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-7063">
          <StatCard label="Frustratie Events (P-12)" value={data.frustrationEvents} icon={<AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-7065" />} data-id="element-7064" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-7066">
          <StatCard label="Guard Rejections (P-3)" value={data.guardData.totalFatalRejections} icon={<ShieldAlert className="w-4 h-4 text-rose-500" data-id="element-7068" />} data-id="element-7067" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-7069">
          <StatCard label="Totaal Vragen" value={data.totalAttempts} icon={<Activity className="w-4 h-4 text-blue-500" data-id="element-7071" />} data-id="element-7070" />
        </motion.div>
      </motion.div>

      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-7072">
        <motion.div variants={itemVariants} data-id="element-7073">
          <Card className="p-5 h-full" data-id="element-7074">
            <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2" data-id="element-7075">
              <BarChart2 className="w-4 h-4 text-indigo-500" data-id="element-7076" />
              Tier Distributie
            </h3>
            <div className="space-y-2" data-id="element-7077">
              {Array.from({
              length: 16
            }, (_, i) => i + 1).map(tier => {
              const count = data.tierCounts[tier] || 0;
              const pct = count / maxTierCount * 100;
              if (count === 0 && tier > 8) return null; // Hide empty higher tiers to save space
              return <div key={tier} className="flex items-center gap-3" data-id="element-7078">
                    <div className="w-12 text-xs font-medium text-slate-500 text-right" data-id="element-7079">
                      Tier {tier}
                    </div>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-7080">
                      <div className="h-full bg-indigo-500 rounded-full" style={{
                    width: `${pct}%`
                  }} data-id="element-7081" />
                    </div>
                    <div className="w-10 text-xs font-semibold text-slate-700" data-id="element-7082">
                      {count}
                    </div>
                  </div>;
            })}
            </div>
          </Card>
        </motion.div>

        <motion.div variants={itemVariants} data-id="element-7083">
          <Card className="p-5 h-full" data-id="element-7084">
            <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2" data-id="element-7085">
              <ShieldAlert className="w-4 h-4 text-rose-500" data-id="element-7086" />
              Pedagogical Guard (Top Redenen)
            </h3>
            {data.guardData.topReasons.length > 0 ? <div className="space-y-4" data-id="element-7087">
                {data.guardData.topReasons.map((reason, i) => <div key={i} data-id="element-7088">
                    <div className="flex justify-between text-sm mb-1" data-id="element-7089">
                      <span className="font-medium text-slate-700" data-id="element-7090">
                        {reason.reason}
                      </span>
                      <span className="font-bold text-slate-900" data-id="element-7091">
                        {reason.count}
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-7092">
                      <div className="h-full bg-rose-500 rounded-full" style={{
                  width: `${data.guardData.totalFatalRejections > 0 ? reason.count / data.guardData.totalFatalRejections * 100 : 0}%`
                }} data-id="element-7093" />
                    </div>
                  </div>)}
                <div className="pt-4 mt-4 border-t border-slate-100" data-id="element-7094">
                  <div className="flex justify-between text-sm" data-id="element-7095">
                    <span className="text-slate-500" data-id="element-7096">
                      Vervangingsratio (Success)
                    </span>
                    <span className="font-bold text-emerald-600" data-id="element-7097">
                      {Math.round(data.guardData.replacementSuccessRate * 100)}%
                    </span>
                  </div>
                </div>
              </div> : <div className="text-sm text-slate-500 text-center py-8" data-id="element-7098">
                Geen guard rejections geregistreerd.
              </div>}
          </Card>
        </motion.div>
      </motion.div>
    </PageLayout>;
}